/**
 * Marketing i18n fragment — ucHrGovernance (use-cases/hr-governed-answers).
 *
 * Source of truth for every claim on this page: docs/hr-governance.md (the HR
 * governance vertical slice, migrations 220 + 222). Only shipped behaviour is
 * described: the three channel adapters (Teams personal chat, Slack DM, the
 * public chatbot API), the sensitivity/assignment authorization model, the
 * four-eyes approval with its server-side 72h TTL and CONSUMED delivery, the
 * data-minimisation gate in front of the model, the two read-only connectors
 * (AFAS Profit, Visma Raet Youforce) with explicit field projection and honest
 * failure, the report/correct/retract loop, and the identifier-only audit
 * trail. No customer, sector or deployment claim is made anywhere on this page.
 */

export const ucHrGovernanceEn = {
  meta: {
    title: "HR questions answered under an HR permission model",
    description:
      "Employees ask an HR question in Teams, Slack or your own chatbot. FixControl decides who may see it, shows the model only the fields it needs, routes sensitive cases to a named reviewer, and requires a second person before an answer is delivered.",
  },
  hero: {
    eyebrow: "HR questions",
    title: "HR questions answered with AI, under",
    highlight: "the permission model HR needs",
    subtitle:
      "An employee of your organization asks about leave, a payslip line or a procedure. FixControl resolves who is asking, which sources that person may be answered from, and how sensitive the question is. A routine answer can be delivered straight away; a sensitive one becomes a case that only an explicitly assigned reviewer can open, and that a second person has to approve before it goes out.",
    primaryCta: "Book a demo",
    secondaryCta: "Talk to us",
    bullet1: "Sensitive cases only for an assigned reviewer",
    bullet2: "Second person approves before delivery",
    bullet3: "AFAS Profit and Visma Raet Youforce read-only",
  },
  video: {
    eyebrow: "The demo",
    title: "Watch the governed HR flow",
    description:
      "From an HR question in Teams to a delivered answer: who may see it, what the HR system is allowed to return, and the second person who has to approve.",
  },
  how: {
    eyebrow: "How it works",
    title: "From a question to an answer someone is accountable for",
    description:
      "Every step is a server-side check, not a setting in the chat window. The channel the question arrives on carries no permissions of its own: Teams, Slack and your chatbot all call the same command, and that command decides.",
  },
  steps: {
    ask: {
      tag: "The question",
      title: "An employee of your organization asks in Teams, Slack or your own chatbot",
      body: "In Microsoft Teams the question is only handled in a personal chat, in Slack only in a direct message with the bot. A shared channel gets a short explanation and nothing is processed — the question text does not even reach the model. Your own chatbot can ask on an employee's behalf through an authenticated API.",
    },
    identity: {
      tag: "Who is asking",
      title: "Identity is resolved before anything else happens",
      body: "The chat account has to be linked to a FixControl user, and that user needs an active membership of an HR-enabled project with permission to ask. An unlinked account gets a link instruction instead of an answer. A missing identity can only make the check stricter, never looser.",
    },
    classify: {
      tag: "Sensitivity",
      title: "The question gets a sensitivity level it cannot lose",
      body: "A deterministic classification sets a safety floor. A model may propose a stricter reading on top of it — more sensitive, or human review required — but it can never argue a question down to a lighter route.",
    },
    minimize: {
      tag: "Minimisation",
      title: "The model is shown the question text, not the file",
      body: "The classification proposal receives the question text and nothing else, fully separated from your organization’s own data. Personnel-file data, retrieved documents and answer bodies are never sent to a model; the answer itself is drafted from templates and server-selected sources.",
    },
    route: {
      tag: "Assignment",
      title: "A sensitive case waits for an explicitly assigned reviewer",
      body: "Someone with the assign permission sees a restricted case as metadata only — status, sensitivity, route — and can hand it only to an active project member whose role carries the permission that sensitivity requires. Assignment is the bridge between a case existing and anyone being allowed to read it.",
    },
    approve: {
      tag: "Approval",
      title: "A second person approves, then the answer is delivered",
      body: "An approval is bound to the exact answer text and version. Whoever requested it can never decide it, the approval expires on a server-side 72-hour window clients cannot stretch, editing the answer invalidates it, and delivery consumes it in a single step so a double click cannot send twice.",
    },
  },
  features: {
    eyebrow: "What you get",
    title: "The parts an HR pilot usually discovers late",
    description:
      "Permissions, minimisation, approval and correction are the product here, not a phase after the answers work.",
    visibility: {
      title: "A visibility contract, not a shared queue",
      body: "The employee who asked sees their own question. Your own HR staff see the work their project role allows. A restricted case is readable only by the reviewer it was explicitly assigned to — tenant administration is not HR access, and a technical administrator sees empty lists.",
    },
    sensitivity: {
      title: "Three sensitivity levels with different routes",
      body: "Normal, sensitive and highly restricted. Medical, legal and confidential employee-relations content takes the restricted human route. Missing, expired or contradicting sources, an unavailable connector, low confidence or an unclear question never produce an invented answer.",
    },
    minimization: {
      title: "Named fields, not a personnel file",
      body: "The connectors read a fixed, explicitly projected set of fields — employment percentage and type, collective agreement, remaining leave hours with an as-of date, a payroll explanation code. A widened view on the HR side cannot leak extra data into an answer.",
    },
    connectors: {
      title: "AFAS Profit and Visma Raet Youforce, read-only",
      body: "Both connectors read and write nothing back. Credentials are per organization, a project has to activate a connector explicitly, and a connection check makes a revoked token visible before an employee runs into it.",
    },
    correction: {
      title: "Report, correct, or withdraw",
      body: "The employee who asked — and your HR staff within what they may already read — can mark an answer as incorrect. A correction is published as a new version while the original is kept and marked superseded. When there is no safe replacement, the answer is withdrawn instead.",
    },
    audit: {
      title: "An audit trail on identifiers, not on content",
      body: "Decisions record project scope, case and answer identifiers, classification, source versions, the policy decision and its reason code. Question text, case summaries, medical content, employee references and answer bodies stay out of the audit record and inside the HR tables.",
    },
  },
  buildBuy: {
    eyebrow: "Build or buy",
    title: "The chatbot is the easy half",
    noteTitle: "Where an internal HR pilot usually stalls",
    noteBody:
      "Many organisations start their own AI pilot for HR questions. The chatbot is there quickly; then the real work begins: who may see which question, what may a model see of a personnel file, who approves a sensitive answer, and how do you show that afterwards? That work is rarely underestimated on purpose — it lands next to everyone's day job, and it needs decisions from HR, privacy, security and IT at the same time. At FixControl that governance foundation is the product, not the finishing touch. You bring the policies, the HR systems and the people who may approve; the permission model, the approval flow and the audit trail are already there.",
  },
  governed: {
    eyebrow: "What the model is allowed to do",
    title: "AI proposes a reading; it decides nothing",
    noteTitle: "The boundary around the model",
    noteBody:
      "The model has no database access and no free HTTP tool. It cannot resolve identity, cannot grant membership, cannot lower a sensitivity level, cannot choose which sources an answer rests on, cannot reach a connector, cannot evaluate the policy, and cannot approve or deliver an answer. Source references come only from rows the server selected. If the model provider is unavailable, the deterministic route is used and the question keeps moving.",
  },
  faq: {
    eyebrow: "Questions HR people ask",
    title: "What an employee and an HR manager want to know",
    q1: {
      q: "Who sees an employee’s question?",
      a: "That employee does, always. For a normal case, the HR colleagues on your team whose project role covers that work see it too. For a sensitive case the circle is smaller: only the reviewer the case was explicitly assigned to can read it. Someone with the permission to assign sees the case exists — status, sensitivity, route — without its content.",
    },
    q2: {
      q: "What happens to a sensitive question?",
      a: "It does not get a direct answer. It becomes a case at a higher sensitivity level, and that level determines which permission a reviewer needs before the case can even be handed to them. Medical, legal and confidential employee-relations questions always take that route.",
    },
    q3: {
      q: "What does the AI see of an employee’s personnel file?",
      a: "The question text, and nothing else. That is what the classification proposal is given, and it is kept fully separate from your organization’s own data. The employee’s file, retrieved documents and the answer body are never sent to a model. When personal data is needed for an answer, the server reads named fields directly from the HR system.",
    },
    q4: {
      q: "Can an answer just go out to the employee?",
      a: "A general, current, non-contradicting policy answer above the confidence threshold can be delivered directly. Anything touching that employee’s own situation becomes a draft or a review case. In that case an approval is required, it is bound to that exact answer text, the person who requested it cannot approve it, and it expires after 72 hours on the server.",
    },
    q5: {
      q: "What if the answer turns out to be wrong?",
      a: "The employee can report it as incorrect, and so can your HR staff within what they may already read. The answer goes under review and a correction task is created. A correction becomes a new version with a link to the original, which is kept and marked superseded. If there is no safe replacement, the answer is withdrawn instead of quietly rewritten.",
    },
    q6: {
      q: "What do the connections to the HR systems read?",
      a: "AFAS Profit and Visma Raet Youforce are connected read-only and write nothing back. Each call filters on one person and projects the response onto a fixed set of fields. If a value is missing or unreadable, no number is invented and no leave balance is defaulted to zero — the question is handed to a person instead.",
    },
    q7: {
      q: "Is what happens recorded?",
      a: "Yes, on identifiers. The record holds project scope, case and answer identifiers, classification, the source versions used, the policy decision and its reason code. Question text, summaries, medical content, employee references and answer bodies are deliberately not in it, so the trail can be reviewed without reopening the case content.",
    },
    q8: {
      q: "Through which channels can an employee ask?",
      a: "Microsoft Teams in a personal chat, Slack in a direct message with the bot, or your own existing chatbot through an authenticated API. The API key is bound to one organization, the chatbot may state who is asking and nothing more, and the same permission checks then run for that employee.",
    },
  },
  related: {
    approvalLedger: {
      label: "AI approval ledger",
      description: "Who approved what, from which channel, on which grounds.",
    },
    teams: {
      label: "Microsoft Teams integration",
      description: "Questions and approvals in Teams, in a personal chat.",
    },
    slack: {
      label: "Slack integration",
      description: "Questions in a direct message, approvals in the thread.",
    },
  },
  cta: {
    title: "See the permission model before the chatbot",
    description:
      "Book a demo and follow one sensitive HR question through assignment, approval, delivery and correction.",
    primary: "Book a demo",
    secondary: "Talk to us",
  },
};

export const ucHrGovernanceNl = {
  meta: {
    title: "HR-vragen beantwoord onder een HR-rechtenmodel",
    description:
      "Medewerkers stellen een HR-vraag in Teams, Slack of je eigen chatbot. FixControl bepaalt wie de vraag mag zien, laat het model alleen de velden zien die nodig zijn, stuurt gevoelige zaken naar een toegewezen behandelaar, en vraagt een tweede persoon om goedkeuring voordat een antwoord de deur uit gaat.",
  },
  hero: {
    eyebrow: "HR-vragen",
    title: "HR-vragen beantwoord met AI, onder",
    highlight: "het rechtenmodel dat HR nodig heeft",
    subtitle:
      "Een medewerker van je organisatie vraagt iets over verlof, een regel op de loonstrook of een procedure. FixControl bepaalt wie de vraag stelt, uit welke bronnen die persoon beantwoord mag worden, en hoe gevoelig de vraag is. Een gewone vraag kan direct beantwoord worden; een gevoelige vraag wordt een zaak die alleen een expliciet toegewezen behandelaar kan openen, en die een tweede persoon moet goedkeuren voordat het antwoord verstuurd wordt.",
    primaryCta: "Boek een demo",
    secondaryCta: "Neem contact op",
    bullet1: "Gevoelige zaken alleen voor een toegewezen behandelaar",
    bullet2: "Tweede persoon keurt goed vóór aflevering",
    bullet3: "AFAS Profit en Visma Raet Youforce alleen-lezen",
  },
  video: {
    eyebrow: "De demo",
    title: "Bekijk de HR-flow onder controle",
    description:
      "Van een HR-vraag in Teams naar een geleverd antwoord: wie het mag zien, wat het HR-systeem mag teruggeven, en de tweede persoon die moet goedkeuren.",
  },
  how: {
    eyebrow: "Hoe het werkt",
    title: "Van een vraag naar een antwoord waar iemand voor tekent",
    description:
      "Elke stap is een controle op de server, geen instelling in het chatvenster. Het kanaal waarop de vraag binnenkomt heeft zelf geen rechten: Teams, Slack en je eigen chatbot roepen hetzelfde commando aan, en dat commando beslist.",
  },
  steps: {
    ask: {
      tag: "De vraag",
      title: "Een medewerker van je organisatie vraagt het in Teams, Slack of je eigen chatbot",
      body: "In Microsoft Teams wordt de vraag alleen in een persoonlijk gesprek behandeld, in Slack alleen in een direct bericht met de bot. In een gedeeld kanaal volgt een korte uitleg en wordt er niets verwerkt — de vraagtekst bereikt het model dan niet eens. Je eigen chatbot kan namens een medewerker vragen via een geauthenticeerde API.",
    },
    identity: {
      tag: "Wie vraagt het",
      title: "Identiteit wordt vastgesteld voordat er iets anders gebeurt",
      body: "Het chataccount moet gekoppeld zijn aan een FixControl-gebruiker, en die gebruiker heeft een actief lidmaatschap nodig van een project waar HR aanstaat, met het recht om te vragen. Een ongekoppeld account krijgt een koppelinstructie in plaats van een antwoord. Een ontbrekende identiteit kan de controle alleen strenger maken, nooit soepeler.",
    },
    classify: {
      tag: "Gevoeligheid",
      title: "De vraag krijgt een gevoeligheidsniveau dat er niet meer af kan",
      body: "Een deterministische classificatie legt een ondergrens vast. Een model mag daar een strengere lezing bovenop voorstellen — gevoeliger, of menselijke review vereist — maar het kan een vraag nooit naar een lichtere route praten.",
    },
    minimize: {
      tag: "Dataminimalisatie",
      title: "Het model ziet de vraagtekst, niet het dossier",
      body: "Het classificatievoorstel krijgt de vraagtekst en verder niets — volledig los van de organisatiegegevens. Dossiergegevens, opgehaalde documenten en antwoordteksten gaan nooit naar een model; het antwoord zelf wordt opgesteld uit sjablonen en bronnen die de server heeft gekozen.",
    },
    route: {
      tag: "Toewijzing",
      title: "Een gevoelige zaak wacht op een expliciet toegewezen behandelaar",
      body: "Wie het toewijsrecht heeft, ziet een afgeschermde zaak alleen als metagegevens — status, gevoeligheid, route — en kan die alleen overdragen aan een actief projectlid met een rol die het recht draagt dat bij die gevoeligheid hoort. Toewijzing is de brug tussen het bestaan van een zaak en het recht om hem te lezen.",
    },
    approve: {
      tag: "Goedkeuring",
      title: "Een tweede persoon keurt goed, daarna gaat het antwoord de deur uit",
      body: "Een goedkeuring is gebonden aan precies die antwoordtekst en versie. Wie de goedkeuring aanvroeg, kan er nooit zelf over beslissen, de goedkeuring vervalt na een venster van 72 uur dat op de server staat en dat clients niet kunnen oprekken, het bewerken van het antwoord maakt hem ongeldig, en het afleveren verbruikt hem in één stap, zodat dubbelklikken niet twee keer verstuurt.",
    },
  },
  features: {
    eyebrow: "Wat je krijgt",
    title: "De onderdelen die een HR-pilot meestal te laat ontdekt",
    description:
      "Rechten, dataminimalisatie, goedkeuring en correctie zijn hier het product, niet een fase nadat de antwoorden werken.",
    visibility: {
      title: "Een zichtbaarheidscontract, geen gedeelde wachtrij",
      body: "De medewerker die de vraag stelde, ziet de eigen vraag. Je eigen HR-medewerkers zien het werk dat hun projectrol toelaat. Een afgeschermde zaak is alleen leesbaar voor de behandelaar aan wie hij expliciet is toegewezen — organisatiebeheer is geen HR-toegang, en een technisch beheerder ziet lege lijsten.",
    },
    sensitivity: {
      title: "Drie gevoeligheidsniveaus met verschillende routes",
      body: "Normaal, gevoelig en sterk beperkt. Medische, juridische en vertrouwelijke arbeidsrelatie-onderwerpen volgen altijd de beperkte menselijke route. Ontbrekende, verlopen of tegenstrijdige bronnen, een onbereikbare koppeling, lage zekerheid of een onduidelijke vraag leveren nooit een verzonnen antwoord op.",
    },
    minimization: {
      title: "Benoemde velden, geen personeelsdossier",
      body: "De koppelingen lezen een vaste, expliciet geprojecteerde set velden — dienstverbandpercentage en -soort, cao, resterende verlofuren met peildatum, een looncode. Een verbreed overzicht aan de HR-kant kan daardoor geen extra gegevens in een antwoord laten lekken.",
    },
    connectors: {
      title: "AFAS Profit en Visma Raet Youforce, alleen-lezen",
      body: "Beide koppelingen lezen en schrijven niets terug. Inloggegevens staan per organisatie, een project moet een koppeling expliciet aanzetten, en een verbindingscontrole maakt een ingetrokken token zichtbaar voordat een medewerker erop stuit.",
    },
    correction: {
      title: "Melden, corrigeren of intrekken",
      body: "De medewerker die de vraag stelde — en je HR-medewerkers binnen wat zij toch al mogen lezen — kan een antwoord als onjuist melden. Een correctie verschijnt als nieuwe versie terwijl het origineel bewaard blijft en als vervangen wordt gemarkeerd. Is er geen veilige vervanging, dan wordt het antwoord ingetrokken.",
    },
    audit: {
      title: "Een audittrail op kenmerken, niet op inhoud",
      body: "Vastgelegd worden projectscope, zaak- en antwoordkenmerken, classificatie, de gebruikte bronversies, het beleidsbesluit en de reden erachter. Vraagtekst, samenvattingen, medische inhoud, medewerkerverwijzingen en antwoordteksten staan er bewust niet in, zodat het spoor te controleren is zonder de zaakinhoud opnieuw te openen.",
    },
  },
  buildBuy: {
    eyebrow: "Zelf bouwen of afnemen",
    title: "De chatbot is de makkelijke helft",
    noteTitle: "Waar een interne HR-pilot meestal vastloopt",
    noteBody:
      "Veel organisaties starten een eigen AI-pilot voor HR-vragen. De chatbot is er snel; dan begint het echte werk: wie mag welke vraag zien, wat mag een model zien van een personeelsdossier, wie keurt een gevoelig antwoord goed, en hoe toon je dat achteraf aan? Dat werk wordt zelden met opzet onderschat — het komt erbij naast het gewone werk, en het vraagt tegelijk besluiten van HR, privacy, security en IT. Bij FixControl is dat governance-fundament het product, niet het sluitstuk. Jij brengt het beleid, de HR-systemen en de mensen die mogen goedkeuren; het rechtenmodel, de goedkeuringsroute en de audittrail liggen er al.",
  },
  governed: {
    eyebrow: "Wat het model mag",
    title: "AI stelt een lezing voor; het beslist niets",
    noteTitle: "De grens om het model heen",
    noteBody:
      "Het model heeft geen databasetoegang en geen vrij HTTP-gereedschap. Het kan geen identiteit vaststellen, geen lidmaatschap verlenen, geen gevoeligheidsniveau verlagen, niet kiezen op welke bronnen een antwoord rust, geen koppeling benaderen, het beleid niet evalueren, en een antwoord niet goedkeuren of afleveren. Bronverwijzingen komen uitsluitend uit rijen die de server heeft geselecteerd. Valt de modelaanbieder weg, dan wordt de deterministische route gebruikt en loopt de vraag gewoon door.",
  },
  faq: {
    eyebrow: "Vragen die HR stelt",
    title: "Wat een medewerker en een HR-manager willen weten",
    q1: {
      q: "Wie ziet de vraag van een medewerker?",
      a: "Die medewerker zelf, altijd. Bij een gewone zaak zien ook de HR-collega's in je organisatie het van wie de projectrol dat werk dekt. Bij een gevoelige zaak is die kring kleiner: alleen de behandelaar aan wie de zaak expliciet is toegewezen kan hem lezen. Wie het recht heeft om toe te wijzen, ziet dát de zaak bestaat — status, gevoeligheid, route — zonder de inhoud.",
    },
    q2: {
      q: "Wat gebeurt er met gevoelige vragen?",
      a: "Die krijgen geen direct antwoord. Ze worden een zaak op een hoger gevoeligheidsniveau, en dat niveau bepaalt welk recht een behandelaar nodig heeft voordat de zaak überhaupt aan hem overgedragen kan worden. Medische, juridische en vertrouwelijke arbeidsrelatievragen volgen altijd die route.",
    },
    q3: {
      q: "Wat ziet de AI van het dossier van een medewerker?",
      a: "De vraagtekst, en verder niets. Dat is wat het classificatievoorstel krijgt, en het staat volledig los van de organisatiegegevens. Het dossier van die medewerker, opgehaalde documenten en de antwoordtekst gaan nooit naar een model. Zijn er persoonsgegevens nodig voor een antwoord, dan leest de server benoemde velden rechtstreeks uit het HR-systeem.",
    },
    q4: {
      q: "Kan een antwoord zomaar naar de medewerker toe?",
      a: "Een algemeen, actueel en niet-tegenstrijdig beleidsantwoord boven de zekerheidsdrempel kan direct afgeleverd worden. Alles wat de eigen situatie van die medewerker raakt, wordt een concept of een reviewzaak. Dan is goedkeuring nodig, die is gebonden aan precies die antwoordtekst, degene die haar aanvroeg kan haar niet zelf goedkeuren, en ze vervalt na 72 uur op de server.",
    },
    q5: {
      q: "Wat als het antwoord fout blijkt?",
      a: "De medewerker kan het als onjuist melden, en je HR-medewerkers kunnen dat binnen wat zij toch al mogen lezen. Het antwoord gaat in review en er ontstaat een correctietaak. Een correctie wordt een nieuwe versie met een link naar het origineel, dat bewaard blijft en als vervangen wordt gemarkeerd. Is er geen veilige vervanging, dan wordt het antwoord ingetrokken in plaats van stil herschreven.",
    },
    q6: {
      q: "Wat lezen de koppelingen met de HR-systemen?",
      a: "AFAS Profit en Visma Raet Youforce zijn alleen-lezen gekoppeld en schrijven niets terug. Elke aanroep filtert op één persoon en projecteert het antwoord op een vaste set velden. Ontbreekt een waarde of is die onleesbaar, dan wordt er geen getal verzonnen en geen verlofsaldo op nul gezet — de vraag gaat dan naar een mens.",
    },
    q7: {
      q: "Wordt vastgelegd wat er gebeurt?",
      a: "Ja, op kenmerken. Vastgelegd worden projectscope, zaak- en antwoordkenmerken, classificatie, de gebruikte bronversies, het beleidsbesluit en de reden erachter. Vraagtekst, samenvattingen, medische inhoud, medewerkerverwijzingen en antwoordteksten staan er bewust niet in, zodat het spoor te controleren is zonder de zaakinhoud opnieuw te openen.",
    },
    q8: {
      q: "Via welke kanalen kan een medewerker vragen stellen?",
      a: "Microsoft Teams in een persoonlijk gesprek, Slack in een direct bericht met de bot, of je eigen bestaande chatbot via een geauthenticeerde API. De API-sleutel is aan één organisatie gebonden, de chatbot mag alleen zeggen wíé er vraagt en verder niets, en daarna lopen dezelfde rechtencontroles voor die medewerker.",
    },
  },
  related: {
    approvalLedger: {
      label: "AI-goedkeuringsgrootboek",
      description: "Wie keurde wat goed, vanuit welk kanaal, op welke gronden.",
    },
    teams: {
      label: "Microsoft Teams-integratie",
      description: "Vragen en goedkeuringen in Teams, in een persoonlijk gesprek.",
    },
    slack: {
      label: "Slack-integratie",
      description: "Vragen in een direct bericht, goedkeuringen in de thread.",
    },
  },
  cta: {
    title: "Zie het rechtenmodel vóór de chatbot",
    description:
      "Boek een demo en volg één gevoelige HR-vraag door toewijzing, goedkeuring, aflevering en correctie.",
    primary: "Boek een demo",
    secondary: "Neem contact op",
  },
};
